import { RoleAreasState, State } from '../configure-store';
import { Role, RolesArea, RolesData } from '../../types/roles';
import { getIsSite } from './config';
import { InviteStatusType, RoleKey } from '../../services/server-types';
import Experiments from '@wix/wix-experiments';

export const getRoleAreas = (state: State): RoleAreasState => state.roleAreas ?? [];

export const getRoles = (state: State): RolesData => {
    return state.roleAreas?.map(roleArea => roleArea.roles).flat() ?? [];
};

export const getRoleScopes = (state: State, roleId: string): string[] =>
  getRoles(state).find(role => role.id === roleId)?.scopes;

export const getAreasWithAssignableRoles = (state: State, experiments?: Experiments, allowedRoles?: string[]): RoleAreasState => {
    const roleAreas = getRoleAreas(state);
    const isSiteLevel = getIsSite(state);
    return roleAreas.map(rolesArea => {
        const filteredRoles = rolesArea.roles.filter(role => shouldDisplayRole(role, isSiteLevel, experiments) || allowedRoles?.includes(role.id));
        if (filteredRoles.length > 0) {
            return {
                title: rolesArea.title,
                roles: filteredRoles,
            };
        } else {
            return null;
        }
    }).filter(rolesArea => rolesArea);
};

export const getUsedAreas = (state: State): RoleAreasState => {
    const { teamMembers } = state;
    const roleAreas = getAreasWithAssignableRoles(state);
    const mapAreaToMembersCount: any = {};
    const members = [...teamMembers.invites.filter(invite => invite.status === InviteStatusType.PENDING), ...teamMembers.users, ...teamMembers.teams];
    members.forEach(member =>
        member.roles.forEach(role => {
        const area = getRoleAreaByRoleId(state, role);
        if (area) {
            if (!mapAreaToMembersCount[area.title]) {
                mapAreaToMembersCount[area.title] = 0;
            }
            mapAreaToMembersCount[area.title]++;
        }
    }));
    return roleAreas.map(area => {
        if (mapAreaToMembersCount[area.title]) {
            return area;
        }
        return null;
    }).filter(area => area);
};

export const getRoleAreaByRoleId = (state: State, roleId: string): RolesArea => state.roleAreas.find(area => area.roles.find(role => role.id === roleId));

const shouldDisplayRole = (role: Role, isSiteLevel: boolean, experiments: Experiments): boolean => {
    if (role.deprecated) {
        return false;
    } else if (isSiteLevel && role.restrictFromLevel === 'site') {
        return false;
    } else if (!isSiteLevel && role.restrictFromLevel === 'account') {
        return false;
    } else if (role.experiments?.length > 0 && (!role.experiments.some(experiment => experiments?.enabled(experiment)))) {
        return false;
    }
    return true;
};

export const getIsCustomRole = (state: State, roleId: string): boolean => getRoles(state).find(role => role.id === roleId)?.isCustom;

export const isOwner = (state: State, roleId: string): boolean =>
  getRoles(state).find(predefinedRole => predefinedRole.key === RoleKey.OWNER)?.id === roleId;

export const getSystemRoles = (state: State) => getRoleAreas(state).find(predefinedArea => predefinedArea.roles.find(role => isOwner(state, role.id)))?.roles;

export const getIsSystemRole = (state: State) => (roleId: string): boolean =>
  getSystemRoles(state)?.some(role => role.id === roleId);

export const getAllScopes = (state: State): string[] =>
  Array.from(new Set(getRoles(state).map(role => role.scopes).flat()));

export const getIsScopePartOfARole = (state: State) => (scope: string): boolean =>
    getAllScopes(state).includes(scope);
